import { useParams } from 'react-router-dom'

import {
  Box,
  Button,
  Grid,
  ProgressBar,
  Spinner,
  Tag,
  TagColor,
  Text,
  useConfirmationDialog,
} from '@factorialco/deprecated-design-system'
import {
  DocumentsSignatureStatusEnum,
  PerformanceAgreementStatusEnum,
  PerformanceProcessAgreementListCardFragment,
  PerformanceProcessBulkInitiateAgreementsDocument,
  PerformanceProcessMyAgreementsDocument,
} from 'generated/resources/graphql'
import map from 'lodash/map'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { AgreementListCard } from 'modules/performance_process/components/agreements/AgreementListCard'
import {
  AgreementStatusTag,
  getSignatureStatus,
} from 'modules/performance_process/components/agreements/StatusTag'
import { useInitiateAgreement } from 'modules/performance_process/lib/agreementDialog'

type AgreementStatus =
  | 'can_initiate'
  | 'pending_signatures'
  | PerformanceAgreementStatusEnum
  | DocumentsSignatureStatusEnum

const groupTargetsByAgreementStatus = (targets: PerformanceProcessAgreementListCardFragment[]) => {
  const grouped: Record<AgreementStatus, PerformanceProcessAgreementListCardFragment[]> = {
    can_initiate: [],
    pending: [],
    pending_signatures: [],
    partially_signed: [],
    bounced_email: [],
    declined: [],
    cancelled: [],
    expired: [],
    error: [],
    signed: [],
    completed: [],
  }

  targets.forEach((target) => {
    const agreement = target.agreement
    const canInitiate = target._policies?.initiate.can

    const signatureStatus = getSignatureStatus(agreement)
    const status =
      signatureStatus === 'pending' ? 'pending_signatures' : signatureStatus ?? agreement?.status

    if (!status && canInitiate) grouped['can_initiate'].push(target)
    if (status) grouped[status].push(target)
  })

  return grouped
}

interface MyAgreementsProps {
  republishEvaluationsEnabled: boolean
}

export const MyAgreements: React.FC<MyAgreementsProps> = ({ republishEvaluationsEnabled }) => {
  const { reviewId } = useParams()
  const { openDialog } = useConfirmationDialog()

  const { data, loading, error } = useQuery(PerformanceProcessMyAgreementsDocument, {
    variables: {
      processId: Number(reviewId),
    },
  })

  const { initiateAgreement } = useInitiateAgreement({ republishEvaluationsEnabled })

  const [bulkInitiateAgreements] = useMutation(PerformanceProcessBulkInitiateAgreementsDocument, {
    refetchQueries: [PerformanceProcessMyAgreementsDocument],
    extractorFn: (mutationData) =>
      mutationData.performanceMutations.bulkInitiatePerformanceAgreement,
    awaitRefetchQueries: true,
  })

  const openBulkInitiateDialog = () => {
    openDialog({
      title: i18n.t('performance_process.agreements.my_agreements.bulk_initiate_dialog.title'),
      description: i18n.t(
        'performance_process.agreements.my_agreements.bulk_initiate_dialog.description'
      ),
      onConfirm: () => bulkInitiateAgreements({ variables: { processId: Number(reviewId) } }),
    })
  }

  const targets = data?.performance.reviewProcessTargets ?? []
  const targetsByAgreementStatus = groupTargetsByAgreementStatus(targets)

  if (loading || error)
    return (
      <Box justifyContent="center" alignItems="center" paddingY="s48" height="full">
        <Spinner />
      </Box>
    )

  return (
    <Box gap="s32">
      <Box gap="s12">
        <Text size="500" weight="semibold" color="grey1000">
          {i18n.t('performance_process.agreements.my_agreements.title_new')}
        </Text>
        <Text.Normal>
          {i18n.t('performance_process.agreements.my_agreements.description')}
        </Text.Normal>
      </Box>

      <ProgressBar
        showLabel
        total={targets.length}
        amount={
          targetsByAgreementStatus['signed'].length + targetsByAgreementStatus['completed'].length
        }
        progressColor="viridian1200"
      />

      {map(
        targetsByAgreementStatus,
        (groupedTargets, status: AgreementStatus) =>
          groupedTargets.length > 0 && (
            <Box gap="s16" key={status}>
              {status === 'can_initiate' ? (
                <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center" gap="s8">
                  <Box flexDirection="column">
                    <Tag type={TagColor.grey} inline capitalize>
                      {i18n.t('performance_process.agreements.my_agreements.category_can_initiate')}{' '}
                      ({groupedTargets.length})
                    </Tag>
                  </Box>
                  <Button onClick={openBulkInitiateDialog}>
                    {i18n.t('performance_process.agreements.my_agreements.bulk_initiate')}
                  </Button>
                </Box>
              ) : (
                <AgreementStatusTag
                  agreement={groupedTargets[0]?.agreement}
                  suffix={`(${groupedTargets.length})`}
                />
              )}

              <Grid variant="medium">
                {groupedTargets.map((target) => (
                  <AgreementListCard
                    target={target}
                    key={target.id}
                    onInitiate={() =>
                      initiateAgreement({
                        targetName: target.access?.fullName ?? '',
                        targetId: target.id,
                        processId: Number(reviewId),
                      })
                    }
                  />
                ))}
              </Grid>
            </Box>
          )
      )}
    </Box>
  )
}
